/**
 * m3 echo Frontend - Benutzer-Tabelle
 *
 * Tabellenkomponente zur Anzeige und Verwaltung von Benutzern.
 */

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { UserWithTenant } from '../../services/admin';

interface UserTableProps {
  users: UserWithTenant[];
  onEdit: (user: UserWithTenant) => void;
  onDelete: (user: UserWithTenant) => void;
  onResetPassword: (user: UserWithTenant) => void;
  onToggleStatus: (user: UserWithTenant) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  onEdit,
  onDelete,
  onResetPassword,
  onToggleStatus,
}) => {
  if (users.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Keine Benutzer gefunden
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>E-Mail</strong></TableCell>
            <TableCell><strong>Vollständiger Name</strong></TableCell>
            <TableCell><strong>Mandant</strong></TableCell>
            <TableCell><strong>Rolle</strong></TableCell>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell><strong>Letzter Login</strong></TableCell>
            <TableCell align="center"><strong>Aktionen</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {user.email}
                </Typography>
              </TableCell>
              <TableCell>{user.full_name || '-'}</TableCell>
              <TableCell>
                {user.tenant ? user.tenant.display_name : <em>Kein Mandant</em>}
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {user.is_admin && (
                    <Chip
                      icon={<AdminPanelSettingsIcon />}
                      label="Admin"
                      color="primary"
                      size="small"
                    />
                  )}
                  {user.role === 'viewer' ? (
                    <Chip
                      icon={<VisibilityIcon />}
                      label="Betrachter"
                      size="small"
                      variant="outlined"
                    />
                  ) : (
                    <Chip
                      label="Editor"
                      size="small"
                      color={user.is_admin ? 'default' : 'success'}
                      variant={user.is_admin ? 'outlined' : 'filled'}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell>
                {user.is_admin ? (
                  // Admin users cannot be deactivated - show non-clickable chip
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Aktiv"
                    color="success"
                    size="small"
                  />
                ) : (
                  // Non-admin users can be toggled
                  <Tooltip title={user.is_active ? 'Klicken zum Deaktivieren' : 'Klicken zum Aktivieren'}>
                    <Chip
                      icon={user.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                      label={user.is_active ? 'Aktiv' : 'Inaktiv'}
                      color={user.is_active ? 'success' : 'default'}
                      size="small"
                      onClick={() => onToggleStatus(user)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>
                {user.last_login
                  ? new Date(user.last_login).toLocaleDateString()
                  : 'Nie'}
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                  <Tooltip title="Benutzer Bearbeiten">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(user)}
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Passwort Zurücksetzen">
                    <IconButton
                      size="small"
                      onClick={() => onResetPassword(user)}
                      color="warning"
                    >
                      <VpnKeyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Benutzer Löschen">
                    <IconButton
                      size="small"
                      onClick={() => onDelete(user)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UserTable;
